import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";
import { VITE_UTILITIES_BASE_URL } from "../../../env";
import { CamelCasedPropertiesDeep } from "type-fest";
import { recursiveCamelCaseCipher } from "../../postgrestApi";
import request from "../../request";
import { ClientOutput } from "../client";

const emissions = {
  useQuery,
  usePut,
};

export default emissions;

const QUERY_KEY = "emissions-summary";

export type SiteEmissionsSummary = CamelCasedPropertiesDeep<
  ClientOutput["utilities"]["SiteEmissions"]
>;
export type UtilityMonthlyCompletion = CamelCasedPropertiesDeep<
  ClientOutput["utilities"]["UtilityMonthlyCompletion"]
>;
export type EmissionsSummarySnakeCase =
  ClientOutput["utilities"]["EmissionsSummary"];
export type EmissionsSummary =
  CamelCasedPropertiesDeep<EmissionsSummarySnakeCase>;
export type DatePeriod = ClientOutput["utilities"]["DatePeriod"];
export type DateRange = CamelCasedPropertiesDeep<
  ClientOutput["utilities"]["DateRange"]
>;

type QueryParams = {
  siteIds?: number[];
  datePeriod: DatePeriod;
  year?: number;
  quarter?: string;
};

// endpoint requires {feature: "Utilities", access: "read"}
const getEmissionsSummary = (
  accountId: number | undefined,
  params: QueryParams,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<EmissionsSummary> =>
  request
    .get(`${VITE_UTILITIES_BASE_URL}/sites/emissions_summary`)
    .set(checkFeatureAccessHeader)
    .query({
      account_id: accountId,
      date_period: params.datePeriod,
      year: params.year,
      ...(params.quarter !== undefined && { quarter: params.quarter }),
      ...(params.siteIds && { site_id: params.siteIds }),
    })
    .then((res) => recursiveCamelCaseCipher(res.body));

function useQuery(
  accountId: number | undefined,
  params: QueryParams,
  options: { enabled?: boolean } = { enabled: true }
) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    async (checkFeatureAccessHeader) =>
      getEmissionsSummary(accountId, params, checkFeatureAccessHeader),
    {
      enabled: accountId !== undefined && options.enabled,
      queryKey: [
        QUERY_KEY,
        accountId,
        params.datePeriod,
        params.year,
        params.quarter,
        params?.siteIds,
      ],
      select: (data) => ({
        ...data,
        sites: data.sites.map((site) => ({
          ...site,
          emissions: {
            ...site.emissions,
            emissionsIntensity: site.emissions.emissionsIntensity * 1000, // convert from MTCO2e -> kgCO2e
          },
        })),
      }),
    }
  );
}

const useInvalidateEmissionsSummaryQuery = (accountId: number) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY, accountId],
    });
};

// endpoint requires {feature: "Utilities", access: "edit"}
const putScope3 = (
  siteId: number,
  requestBody: { year: number; emissions: number },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .put(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/emissions/scope_3`)
    .set(checkFeatureAccessHeader)
    .send(requestBody);

function usePut(siteId: number | null, accountId: number) {
  const invalidateEmissionsSummaryQuery =
    useInvalidateEmissionsSummaryQuery(accountId);

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (
      requestBody: { year: number; emissions: number },
      checkFeatureAccessHeader
    ) => {
      if (!siteId) throw new Error("Site ID is not defined");
      return putScope3(siteId, requestBody, checkFeatureAccessHeader);
    },
    {
      onSuccess: async () => await invalidateEmissionsSummaryQuery(),
    }
  );
}
